import React, { FunctionComponent, MutableRefObject } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { SafeAreaView } from 'react-native-safe-area-context'

import { StatusBar } from 'expo-status-bar'
import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  getVideoStatusesLabel,
  Review,
  VideoLesson,
  VideoStatuses,
  VideoStatusesLabel,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/types'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { ActivityIcon } from '../../atoms/icons/ActivityIcon'
import { Link } from '../../atoms/link/Link'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { TruncatedText } from '../../atoms/truncate/TruncatedText'
import { IS_WEB } from '../../constants'
import { ActivityItem } from '../../molecules/activityItem/ActivityItem'
import { LessonHeader } from '../../molecules/lessonHeader/LessonHeader'
import { AuroraFooterWrapper } from '../../organisms/auroraFooterWrapper/AuroraFooterWrapper'
import { VideoList } from '../../organisms/videoList/VideoList'
import { getAuroraAwareBackgroundColor } from '../../styles/commonStyles'
import { TitleSize } from '../../styles/typeStyles'
import { Flex1View } from '../../templates/content/CommonViews'
import { openUrl } from '../../utils/expo-linking'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface LessonOverviewProps {
  id: string
  lessonName?: string
  thumbnail?: string
  instructions?: string | null
  onBackPress: () => void
  videos: VideoLesson[]
  ctaTitle: string | React.ReactNode
  onCTAPressed?: (id: string) => void
  onVideoPressed: (id: string, videoPropertyName?: string) => void
  review?: Review | null
  onReviewPressed: (propertyName: string, status: string) => void
  reviewAvailable: boolean
  topInset?: number
  bottomInset?: number
  accessibilityLabel?: string
  showCTAOnActivityItem?: number
  a11yFocusedElementOnLoad?: MutableRefObject<any>
  isProvider?: boolean
  isPsaBannerVisible?: boolean
}

const LessonOverviewContainer = styled(Flex1View)<{ theme: ThemeType }>(({ theme: { colors, isAuroraTheme } }) => ({
  backgroundColor: getAuroraAwareBackgroundColor(isAuroraTheme, colors.backgroundSecondary),
  height: '100%',
}))

const ScrollContainer = styled.ScrollView<{ bottomInset?: number }>(({ bottomInset = 0 }) => ({
  paddingBottom: `${bottomInset + 50 + 30}px`, // 50 represents button height
}))

const FooterContainer = styled.View({
  position: 'absolute',
  bottom: '0px',
  width: '100%',
})

const ButtonContainer = styled(SafeAreaView)<{ theme: ThemeType }>(({ theme: { spacing, colors, isAuroraTheme } }) => ({
  padding: `${spacing['16px']} ${spacing['16px']}`,
  borderTopWidth: isAuroraTheme ? '0px' : '1px',
  borderTopColor: colors.borderDefault,
  backgroundColor: getAuroraAwareBackgroundColor(isAuroraTheme, colors.backgroundPrimary),
}))

const ScrollContainerContent = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  margin: `0px ${spacing['24px']}`,
  paddingBottom: !IS_WEB ? spacing['72px'] : spacing['12px'],
  alignItems: 'center',
}))

const LessonOverviewContent = styled.View({
  width: '100%',
  maxWidth: '466px',
})

const TruncatedTextContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing, breakpoints } }) => ({
  margin: `${breakpoints.isMinWidthTablet ? spacing['40px'] : spacing['24px']} 0px ${spacing['24px']}`,
}))

const PrimaryButtonComponent = styled(PrimaryButton)<{ theme: ThemeType }>(() => ({
  width: '100%',
  maxWidth: '466px',
  alignSelf: 'center',
}))

const ANGLE_BRACKET_LINK_PATTERN = /<https?:\/\/[^>\s]+>/i
const ANGLE_BRACKET_LINK_REGEX = /<(https?:\/\/[^>\s]+)>/gi

const getInstructionsWithLinks = (instructions: string) => (
  <>
    {instructions
      .split(ANGLE_BRACKET_LINK_REGEX)
      .map((text, index) =>
        index % 2 === 1 ? <Link key={`${text}-${index}`} text={text} onPress={() => openUrl(text)} /> : text,
      )}
  </>
)

/**
 * After tapping on a lesson, this is the page the client will see for all the videos
 * a lesson contains and an overview of the lesson
 */
export const LessonOverview: FunctionComponent<LessonOverviewProps> = ({
  id,
  lessonName = '',
  thumbnail = '',
  instructions = '',
  onBackPress,
  review,
  onReviewPressed,
  ctaTitle,
  onCTAPressed,
  onVideoPressed,
  reviewAvailable,
  topInset = 0,
  bottomInset = 0,
  videos = [],
  showCTAOnActivityItem,
  accessibilityLabel,
  children,
  a11yFocusedElementOnLoad,
  isProvider = false,
  isPsaBannerVisible = false,
}) => {
  const {
    breakpoints: { isMinWidthTablet },
    colors,
    isAuroraTheme,
  } = useTheme()
  const { formatMessage } = useIntl()
  const activityItemTitle = !!review ? getVideoStatusesLabel(review?.reviewCompletionStatus) : ''
  const isReviewItemCompleted = activityItemTitle === VideoStatusesLabel.get(VideoStatuses.completed)
  const instructionText = instructions ?? ''
  const hasAngleBracketLink = ANGLE_BRACKET_LINK_PATTERN.test(instructionText)
  const normalizedInstructionText = hasAngleBracketLink
    ? instructionText.replace(ANGLE_BRACKET_LINK_REGEX, '$1')
    : instructionText
  const instructionsWithLinks = hasAngleBracketLink
    ? getInstructionsWithLinks(instructionText)
    : normalizedInstructionText
  const withCTAOnActivityItem = isMinWidthTablet
    ? showCTAOnActivityItem === videos.length && !isReviewItemCompleted && reviewAvailable && IS_WEB
    : false
  const hasBottomCTA = !isMinWidthTablet && !isReviewItemCompleted
  const getCTAOnPress = () => {
    // if all videos are completed, then return the lesson review function
    if (showCTAOnActivityItem === videos.length && !!review) {
      return onReviewPressed(review.reviewPropertyName, review.reviewCompletionStatus)
    } else {
      // for mobile CTA function
      if (!IS_WEB && onCTAPressed) {
        return onCTAPressed(id)
      } else {
        return onVideoPressed(videos[showCTAOnActivityItem!].videoId, videos[showCTAOnActivityItem!].videoPropertyName)
      }
    }
  }
  const footerCTA = (
    <ButtonContainer edges={['bottom']} testID={tID('Footer-CTA')}>
      <PrimaryButtonComponent
        text={ctaTitle}
        onPress={getCTAOnPress}
        accessibilityLabel={accessibilityLabel}
        fullWidth
      />
    </ButtonContainer>
  )

  return (
    <LessonOverviewContainer testID={tID('LessonOverview')}>
      <ScrollContainer bottomInset={bottomInset}>
        <LessonHeader
          topInset={topInset}
          id={id}
          lessonName={lessonName}
          thumbnail={thumbnail}
          onBackPress={onBackPress}
          a11yFocusedElementOnLoad={a11yFocusedElementOnLoad}
        />
        <ScrollContainerContent>
          <LessonOverviewContent>
            <TruncatedTextContainer>
              {IS_WEB && isMinWidthTablet ? (
                <BodyText size={BodyTextSize.DEFAULT} text={instructionsWithLinks} />
              ) : (
                <TruncatedText
                  collapseExpandColor={colors.textActive}
                  collapseExpandFontWeight={'normal'}
                  collapseExpandText={[
                    formatMessage({
                      defaultMessage: 'less',
                      description: 'Read less for truncated lesson instruction content',
                    }),
                    formatMessage({
                      defaultMessage: 'more',
                      description: 'Read more for truncated lesson instruction content',
                    }),
                  ]}
                  fontColor={colors.textPrimary}
                  maxNumberOfLines={3}
                  text={instructionsWithLinks}
                  textOverride={normalizedInstructionText}
                  testIDPrefix='LessonOverview-instructions'
                  toggleCollapseExpand
                  collapseExpandTextSize={BodyTextSize.DEFAULT}
                />
              )}
            </TruncatedTextContainer>
            <VideoList
              videos={videos}
              onVideoPressed={onVideoPressed}
              withCTA={!hasBottomCTA}
              showCTAOnVideoItem={showCTAOnActivityItem}
              ctaTitle={ctaTitle}
              withProgressBar
              videoTitleSize={showCTAOnActivityItem ? TitleSize.SMALL : TitleSize.XSMALL}
              accessibilityLabel={accessibilityLabel}
              isPressable={isProvider}
            />
            {review && (
              <ActivityItem
                id={`id-${review.reviewPropertyName}`}
                title={
                  <FormattedMessage defaultMessage='Lesson review' description='Lesson review main button label' />
                }
                subtitle={activityItemTitle}
                onPress={
                  reviewAvailable
                    ? () => onReviewPressed(review.reviewPropertyName, review.reviewCompletionStatus)
                    : noop
                }
                withCTA={withCTAOnActivityItem}
                ctaTitle={ctaTitle}
                icon={<ActivityIcon size={80} />}
                disabled={!isProvider && (!reviewAvailable || (showCTAOnActivityItem ?? 0) !== videos.length)}
                headline={
                  <FormattedMessage defaultMessage='Review' description='Lesson review button headline label' />
                }
                withDisplay
                time={review.reviewTime}
              />
            )}
          </LessonOverviewContent>
        </ScrollContainerContent>
      </ScrollContainer>
      {hasBottomCTA && (
        <FooterContainer>
          {isAuroraTheme ? <AuroraFooterWrapper>{footerCTA}</AuroraFooterWrapper> : footerCTA}
        </FooterContainer>
      )}
      {children}
      <StatusBar style={isPsaBannerVisible ? 'dark' : 'light'} />
    </LessonOverviewContainer>
  )
}
